import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import './RespondToSuggestion.css';

const API = process.env.REACT_APP_API_BASE_URL;

// Public, no-login, read-only view of the recipient's OWN profile — reached
// from the /respond page via the same response-page token, so someone who
// hasn't logged in yet can still check what they look like to others.
// Editing requires a real login (see the link at the bottom) — this page
// has no write path at all. Backed by GET /api/respond/my-profile.
const ProfilePreview = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);

  useEffect(() => {
    if (!token) { setError('This link looks incomplete — please use the link from your email directly.'); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/respond/my-profile?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) { setError(data.error || 'This link is invalid or has expired.'); return; }
        setProfile(data);
      } catch (err) {
        if (!cancelled) setError('Something went wrong loading your profile — please try again shortly.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (error) {
    return (
      <div className="respond-page">
        <div className="respond-page-inner">
          <div className="respond-card respond-card--message">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="respond-page">
        <div className="respond-page-inner">
          <div className="respond-card respond-card--message">
            <p>Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="respond-page">
      <div className="respond-page-inner">
        {profile.communityImage && (
          <div className="respond-hero-strip">
            <div className="respond-hero-strip-text">
              <div className="respond-hero-strip-title">{profile.communityName || 'Your community'}</div>
              <div className="respond-hero-strip-brand">Interconnector</div>
            </div>
            <img src={profile.communityImage} alt="" className="respond-hero-strip-thumb" />
          </div>
        )}
        <h1 className="respond-heading">Your profile in {profile.communityName || 'this community'}</h1>
        <div className="respond-intro-sub">This is a read-only preview of what others see. <Link to="/login" style={{ textDecoration: 'underline' }}>Log in</Link> to make any changes.</div>

        <div className="respond-card">
          <div className="respond-photo-gallery">
            {profile.photos && profile.photos.length > 0 ? (
              profile.photos.map((photo, i) => (
                <img
                  key={i}
                  src={photo}
                  alt=""
                  className={`respond-img respond-img--clickable${i === 0 ? ' respond-img--lead' : ''}`}
                  onClick={() => setEnlargedPhoto(photo)}
                />
              ))
            ) : (
              <div className="respond-img-placeholder">?</div>
            )}
          </div>
          <div className="respond-details">
            <div className="respond-headline">
              <strong>{profile.firstName || 'Someone'}</strong>
              {profile.age != null && <span className="respond-age">{profile.age}</span>}
              {profile.gender && <span className="respond-meta"> · {profile.gender}</span>}
            </div>
            {profile.location && <div className="respond-meta">{profile.location}</div>}
            {profile.discipline && <div className="respond-meta"><strong>Discipline:</strong> {profile.discipline}</div>}
            {profile.experienceLevel && <div className="respond-meta"><strong>Experience Level:</strong> {profile.experienceLevel}</div>}
            {profile.lookingFor && <div className="respond-meta"><strong>Looking For:</strong> {profile.lookingFor}</div>}
            {profile.bio && <div className="respond-bio">{profile.bio}</div>}
          </div>
        </div>

        <div className="respond-alert">
          - Don't forget to let us know who you want to connect with..
          <div className="respond-actions" style={{ marginTop: '0.75rem', justifyContent: 'center' }}>
            <Link to={`/respond?token=${encodeURIComponent(token)}`} className="buttonSecondary">← Go back to suggestions</Link>
          </div>
        </div>
        <div className="respond-intro-sub" style={{ textAlign: 'center', marginTop: '1rem' }}>
          You can also just go to theinterconnector.com directly and log in there :)
        </div>
      </div>

      {enlargedPhoto && (
        <div className="respond-photo-overlay" onClick={() => setEnlargedPhoto(null)}>
          <img src={enlargedPhoto} alt="" className="respond-photo-overlay-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default ProfilePreview;
